import { useCallback, useEffect, useRef } from 'react';
import { useChatStore, Message, Conversation, ToolCall } from '@/stores/chatStore';
import { useUIStore } from '@/stores/uiStore';
import { useSettingsStore, type ModelProvider } from '@/stores/settingsStore';
import { useAuthStore } from '@/stores/authStore';
import { useWebSocket } from './useWebSocket';
import { api } from '@/services/api';

export function useChat() {
  const {
    conversations,
    currentConversation,
    messages,
    isStreaming,
    setConversations,
    setCurrentConversation,
    setMessages,
    addMessage,
    appendToMessage,
    updateMessage,
    addToolCall,
    updateToolCall,
    updateToolCallByStep,
    setIsStreaming,
    addConversation,
    clearMessages,
  } = useChatStore();

  const { setIsThinking, setJarvisActivity, setIsSpeaking } = useUIStore();
  const modelPreference = useSettingsStore((s) => s.modelPreference);
  const voiceEnabled = useSettingsStore((s) => s.voiceEnabled);

  // Track the current streaming message ID so token chunks can append
  const streamingMsgId = useRef<string | null>(null);
  // Track the current active tool call ID for result matching
  const activeToolId = useRef<string | null>(null);
  // Track last error to deduplicate rapidly repeating errors
  const lastErrorRef = useRef<string>('');

  const handleWsMessage = useCallback(
    (data: unknown) => {
      const msg = data as Record<string, unknown>;

      switch (msg.type) {
        // Backend sends type="start" with conversation_id + message_id
        case 'start': {
          const msgId = String(msg.message_id);
          streamingMsgId.current = msgId;

          // Capture conversation_id so follow-up messages have context.
          // Also push it onto the sidebar list immediately — without this,
          // the new conversation only appears after a manual refresh.
          if (msg.conversation_id && !currentConversation) {
            const newConv: Conversation = {
              id: String(msg.conversation_id),
              title: 'New Conversation',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              message_count: 1,
            };
            setCurrentConversation(newConv);
            // Optimistic insert at the top of the sidebar.
            const store = useChatStore.getState();
            if (!store.conversations.find((c) => c.id === newConv.id)) {
              store.addConversation(newConv);
            }
          }

          const assistantMsg: Message = {
            id: msgId,
            role: 'assistant',
            content: '',
            timestamp: new Date().toISOString(),
            isStreaming: true,
          };
          addMessage(assistantMsg);
          setIsStreaming(true);
          setIsThinking(false);
          setJarvisActivity(0.7);
          break;
        }

        // Backend sends type="token" with content delta
        case 'token': {
          const id = streamingMsgId.current;
          if (id && msg.content) {
            appendToMessage(id, msg.content as string);
          }
          break;
        }

        // Backend sends type="thinking" — model reasoning (not shown as content)
        case 'thinking': {
          const id = streamingMsgId.current;
          if (id && msg.content) {
            const store = useChatStore.getState();
            const existing = store.messages.find((m) => m.id === id);
            const prev = existing?.thinkingContent || '';
            updateMessage(id, undefined, undefined);
            // Append thinking content
            useChatStore.setState((state) => ({
              messages: state.messages.map((m) =>
                m.id === id ? { ...m, thinkingContent: prev + (msg.content as string) } : m
              ),
            }));
          }
          break;
        }

        // Backend sends type="widgets" with inline widget data
        case 'widgets': {
          const id = streamingMsgId.current;
          console.log('[JARVIS] Widget chunk received:', { id, widgets: msg.widgets });
          if (id && msg.widgets) {
            const widgetData = msg.widgets as Message['widgets'];
            useChatStore.setState((state) => ({
              messages: state.messages.map((m) =>
                m.id === id ? { ...m, widgets: widgetData } : m
              ),
            }));
            console.log('[JARVIS] Widgets attached to message:', id, widgetData);
          } else {
            console.warn('[JARVIS] Widget chunk dropped:', { hasId: !!id, hasWidgets: !!msg.widgets });
          }
          break;
        }

        // Backend sends type="end" with done=true (may also include widgets)
        case 'end': {
          const id = streamingMsgId.current;
          if (id) {
            updateMessage(id, undefined, false);
            // Attach widgets if included in end chunk
            const endWidgets = msg.widgets as Message['widgets'] | undefined;
            // Mark thinking as done + attach widgets
            useChatStore.setState((state) => ({
              messages: state.messages.map((m) =>
                m.id === id ? { ...m, isThinkingDone: true, ...(endWidgets ? { widgets: endWidgets } : {}) } : m
              ),
            }));
          }
          streamingMsgId.current = null;
          setIsStreaming(false);
          setJarvisActivity(0.2);
          break;
        }

        case 'audio_response': {
          setIsSpeaking(true);
          const audio = new Audio(msg.audio_url as string);
          audio.onended = () => {
            setIsSpeaking(false);
            setJarvisActivity(0.1);
          };
          audio.play().catch(() => setIsSpeaking(false));
          break;
        }

        case 'audio_binary': {
          setIsSpeaking(true);
          const rawBlob = msg.blob as Blob;
          // Ensure blob has audio MIME type for browser playback
          const blob = rawBlob.type ? rawBlob : new Blob([rawBlob], { type: 'audio/wav' });
          const audioUrl = URL.createObjectURL(blob);
          const binaryAudio = new Audio(audioUrl);
          binaryAudio.onended = () => {
            setIsSpeaking(false);
            setJarvisActivity(0.1);
            URL.revokeObjectURL(audioUrl);
          };
          binaryAudio.play().catch(() => {
            setIsSpeaking(false);
            URL.revokeObjectURL(audioUrl);
          });
          break;
        }

        // Backend sends proactive notification from heartbeat
        case 'notification': {
          const title = (msg.title as string) || 'JARVIS';
          const body = (msg.body as string) || '';
          // Show as a system message in chat
          addMessage({
            id: `notif-${Date.now()}`,
            role: 'assistant',
            content: `**${title}**\n${body}`,
            timestamp: new Date().toISOString(),
          });
          // Browser notification if permitted
          if (Notification.permission === 'granted') {
            new Notification(title, { body, icon: '/favicon.png' });
          }
          break;
        }

        // Backend sends type="replace" — corrected text with tool tags stripped
        case 'replace': {
          const id = streamingMsgId.current;
          if (id && msg.content) {
            updateMessage(id, msg.content as string);
          }
          break;
        }

        // Backend sends type="tool_call" with tool + tool_arg
        case 'tool_call': {
          const tool = msg.tool as string;
          const arg = msg.tool_arg as string;

          // System tool calls (embedded tag-based)
          if (tool === 'SWITCH_MODEL') {
            const validProviders = ['claude', 'gemini', 'stark_protocol'];
            if (validProviders.includes(arg)) {
              useSettingsStore.getState().setModelPreference(arg as ModelProvider);
            }
          } else if (tool === 'TOGGLE_VOICE') {
            useSettingsStore.getState().setVoiceEnabled(arg === 'on');
          } else {
            // Agentic tool call — track as structured data
            const id = streamingMsgId.current;
            if (id) {
              // step_number is the backend's 1-based agentic step index. Storing
              // it on the card lets tool_result finalize the RIGHT card even when
              // a round runs tools in parallel (tool_call A, tool_call B, then
              // results in any order). Without it the single activeToolId cursor
              // is overwritten by B and card A spins forever.
              const stepNumber =
                typeof msg.step_number === 'number' ? msg.step_number : undefined;
              const tcId = `tc-${Date.now()}-${stepNumber ?? tool}-${tool}`;
              activeToolId.current = tcId;
              addToolCall(id, { id: tcId, tool, args: arg, status: 'running', stepNumber });
            }
            setJarvisActivity(0.9);
          }
          break;
        }

        // Backend sends type="tool_result" with tool + content (+ step_number)
        case 'tool_result': {
          const id = streamingMsgId.current;
          if (id) {
            const stepNumber =
              typeof msg.step_number === 'number' ? msg.step_number : undefined;
            if (stepNumber !== undefined) {
              // Parallel-safe: finalize the card whose step matches, not the
              // most-recently-started one. The backend emits a terminal
              // tool_result for every open step, so each card is closed by its
              // own step_number.
              updateToolCallByStep(id, stepNumber, 'done');
              // Clear the fallback cursor only if it pointed at this same step,
              // so a later legacy (no step_number) result can't re-close it.
              const store = useChatStore.getState();
              const closed = store.messages
                .find((m) => m.id === id)
                ?.toolCalls?.find((tc) => tc.stepNumber === stepNumber);
              if (closed && activeToolId.current === closed.id) {
                activeToolId.current = null;
              }
            } else if (activeToolId.current) {
              // Legacy stream without step_number — fall back to the single
              // cursor so older turns keep working unchanged.
              updateToolCall(id, activeToolId.current, 'done');
              activeToolId.current = null;
            }
          }
          setJarvisActivity(0.7);
          break;
        }

        // Backend sends type="error" with error field
        case 'error': {
          // Mark the streaming message as done (not streaming anymore)
          const errMsgId = streamingMsgId.current;
          if (errMsgId) {
            updateMessage(errMsgId, undefined, false);
          }
          setIsStreaming(false);
          setIsThinking(false);
          setJarvisActivity(0);
          streamingMsgId.current = null;

          const errorText = (msg.error as string) || 'An error occurred';

          // Auth errors: log out and redirect to login — don't spam messages
          const isAuthError =
            errorText.includes('Invalid token') ||
            errorText.includes('Token required') ||
            errorText.includes('Authentication failed');
          if (isAuthError) {
            useAuthStore.getState().logout();
            break;
          }

          // Deduplicate: skip if same error text as the last one
          if (errorText === lastErrorRef.current) break;
          lastErrorRef.current = errorText;
          // Reset dedup after 5s so the same error can appear again later
          setTimeout(() => { lastErrorRef.current = ''; }, 5000);

          const errorMsg: Message = {
            id: `error-${Date.now()}`,
            role: 'system',
            content: errorText,
            timestamp: new Date().toISOString(),
          };
          addMessage(errorMsg);
          break;
        }
      }
    },
    [addMessage, appendToMessage, updateMessage, addToolCall, updateToolCall, updateToolCallByStep, setIsStreaming, setIsThinking, setJarvisActivity, setIsSpeaking, currentConversation, setCurrentConversation]
  );

  const { send, isConnected } = useWebSocket(handleWsMessage);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) return;

      const userMessage: Message = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: content.trim(),
        timestamp: new Date().toISOString(),
      };

      addMessage(userMessage);
      setIsThinking(true);
      setJarvisActivity(0.5);

      // Backend ChatRequest expects "message" not "content"
      const modelOverride = useSettingsStore.getState().modelOverride;
      send({
        message: content.trim(),
        conversation_id: currentConversation?.id,
        model_provider: modelPreference,
        model: modelOverride || undefined,
        voice_enabled: voiceEnabled,
        // API unification Slice A: declare the channel explicitly. Byte-identical
        // — the server already resolves an absent channel to "web"
        // (validate_channel(None) === "web"), so this only makes the existing
        // default explicit rather than changing any resolved behavior.
        channel: 'web',
      });

      // If WS is not connected, show an error (no REST fallback endpoint)
      if (!isConnected) {
        setIsThinking(false);
        setJarvisActivity(0);
        addMessage({
          id: `error-${Date.now()}`,
          role: 'system',
          content: 'Not connected to server. Please refresh the page.',
          timestamp: new Date().toISOString(),
        });
      }
    },
    [addMessage, currentConversation, isConnected, send, setIsThinking, setJarvisActivity, modelPreference, voiceEnabled]
  );

  const createConversation = useCallback(async () => {
    try {
      const conversation = await api.post<Conversation>('/conversations', {
        title: 'New Conversation',
      });
      addConversation(conversation);
      setCurrentConversation(conversation);
      clearMessages();
      return conversation;
    } catch {
      return null;
    }
  }, [addConversation, setCurrentConversation, clearMessages]);

  const loadConversation = useCallback(
    async (id: string) => {
      try {
        const [conversation, rawMessages] = await Promise.all([
          api.get<Conversation>(`/conversations/${id}`),
          api.get<any[]>(`/conversations/${id}/messages`),
        ]);
        setCurrentConversation(conversation);
        // Map backend MessageResponse (created_at) to frontend Message (timestamp)
        const mapped: Message[] = rawMessages.map((m) => ({
          id: String(m.id),
          role: m.role,
          content: m.content,
          timestamp: m.created_at || m.timestamp || new Date().toISOString(),
        }));
        setMessages(mapped);
      } catch {
        // Hydrated placeholder pointed at a conversation the server can't
        // serve (deleted, ACL'd out, network blip). Drop it so the user
        // doesn't get stuck on a phantom chat.
        setCurrentConversation(null);
      }
    },
    [setCurrentConversation, setMessages]
  );

  const loadConversations = useCallback(async () => {
    try {
      // Backend defaults to limit=20; we want the full sidebar history.
      // 100 is the server's hard ceiling — if Carter ever crosses 100
      // conversations we'll wire infinite scroll.
      const data = await api.get<{ conversations: Conversation[]; total: number }>('/conversations?limit=100');
      // Backend returns { conversations: [...], total } wrapper
      setConversations(data.conversations);
    } catch {
      /* silently fail */
    }
  }, [setConversations]);

  useEffect(() => {
    loadConversations().then(() => {
      // The store hydrates a placeholder Conversation from localStorage on
      // import (id only, empty title) so the first render keeps routing on
      // the conversation view. We still need to fetch the full record +
      // messages on mount; do that whenever the active conversation has no
      // messages yet, regardless of whether it's a placeholder or a real
      // record left over from before unmount.
      const active = useChatStore.getState().currentConversation;
      if (active?.id && useChatStore.getState().messages.length === 0) {
        loadConversation(active.id);
      } else if (!active) {
        const savedId = localStorage.getItem('jarvis_active_conversation');
        if (savedId) {
          loadConversation(savedId);
        }
      }
    });
  }, [loadConversations, loadConversation]);

  return {
    conversations,
    currentConversation,
    messages,
    isStreaming,
    isConnected,
    sendMessage,
    createConversation,
    loadConversation,
    loadConversations,
  };
}
